import React from 'react';

interface VoiceModePanelProps {
  isListening: boolean;
  isSpeaking: boolean;
  isAwaitingReply?: boolean;
  loading: boolean;
}

/**
 * Minimal voice-mode surface — status orb instead of a scrolling transcript.
 */
export default function VoiceModePanel({
  isListening,
  isSpeaking,
  isAwaitingReply = false,
  loading,
}: VoiceModePanelProps) {
  const isThinking = loading || isAwaitingReply;
  let statusLabel = 'Ready when you are';
  let subLabel = 'Speak naturally — I am listening for you';
  let orbBackground = 'linear-gradient(135deg, #4a423c 0%, #7a6e62 100%)';
  let pulse = false;

  if (isThinking) {
    statusLabel = 'Thinking…';
    subLabel = 'Working on your booking';
    orbBackground = 'linear-gradient(135deg, #3a3530 0%, #6b5f54 100%)';
  } else if (isSpeaking) {
    statusLabel = 'Speaking…';
    subLabel = 'Listen for my reply';
    orbBackground = 'linear-gradient(135deg, #1b6b35 0%, #2d7a4a 100%)';
  } else if (isListening) {
    statusLabel = 'Listening…';
    subLabel = 'Say what you would like to book';
    orbBackground = 'linear-gradient(135deg, #8b2e2e 0%, #c0392b 100%)';
    pulse = true;
  }

  return (
    <div style={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px',
      textAlign: 'center',
    }}>
      <style>{`
        @keyframes voice-orb-pulse {
          0% { box-shadow: 0 0 0 0 rgba(192,57,43,0.35); transform: scale(1); }
          70% { box-shadow: 0 0 0 18px rgba(192,57,43,0); transform: scale(1.03); }
          100% { box-shadow: 0 0 0 0 rgba(192,57,43,0); transform: scale(1); }
        }
      `}</style>
      <div style={{
        width: 112,
        height: 112,
        borderRadius: '50%',
        background: orbBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
        animation: pulse ? 'voice-orb-pulse 1.6s ease-out infinite' : 'none',
        boxShadow: '0 8px 32px rgba(45,41,37,0.18)',
      }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="9" y="3" width="6" height="11" rx="3" fill="#f5f2ee" />
          <path
            d="M5 11a7 7 0 0 0 14 0M12 18v3"
            stroke="#f5f2ee"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, color: '#2d2925', marginBottom: 8 }}>
        {statusLabel}
      </div>
      <div style={{ fontSize: 14, color: '#6b5f54', lineHeight: 1.5, maxWidth: 260 }}>
        {subLabel}
      </div>
    </div>
  );
}
